import { P2GBox, P2GText } from '@parcel2go/core-ui';
import { FixedColumn } from '../../../FixedColumn';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useTaxName } from '@parcel2go/npm-p2g-app';

interface props {
  breakdown: Record<string, number>;
  currencyCode: string;
}

const BreakdownKeys = {
  BasePrice: 'Breakdown.BasePrice',
  Protection: 'Breakdown.Protection',
  Subtotal: 'Breakdown.Subtotal',
  SameDayCollection: 'Breakdown.SameDayCollection',
  DDPAdminFee: 'Breakdown.DDPAdminFee',
  IOSSAdminFee: 'Breakdown.IOSSAdminFee',
  CustomsHandlingFee: 'Breakdown.CustomsHandlingFee',
  CustomsHandlingFeeVat: 'Breakdown.CustomsHandlingFeeVat',
  Discount: 'Breakdown.Discount',
  Tax: 'Breakdown.Tax',
  DutiesAndTaxes: 'Breakdown.DutiesAndTaxes',
  LocalTax: 'Breakdown.LocalTax',
  Total: 'Breakdown.Total'
};

const BreakdownRow = ({
  key,
  value,
  defaultValue,
  currencyCode,
  taxName,
  pad
}: {
  key: string;
  value: number;
  defaultValue: string;
  currencyCode: string;
  taxName: string;
  pad?: boolean;
}) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const translationKey = `PROVIDER_GROUP_${key}`.toUpperCase().replace('.', '_');

  if (!value) {
    return null;
  }

  return (
    <P2GBox my={pad ? 1 : 0}>
      <FixedColumn
        left={
          <P2GText variant="body2">
            {t(translationKey, {
              defaultValue: defaultValue,
              taxName: taxName
            })}
          </P2GText>
        }
        right={
          <P2GBox textAlign="right">
            <P2GText variant="body2" disableSmallUnit>
              {toDisplayCurrency(currencyCode, value)}
            </P2GText>
          </P2GBox>
        }
        width={undefined}
        spacing={1}
      />
    </P2GBox>
  );
};

const ProviderGroupSummaryTotalVerified: React.FC<props> = ({ breakdown, currencyCode }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();

  return (
    <P2GBox sx={(t) => ({ borderRadius: t.spacing(1), overflow: 'hidden' })}>
      <P2GBox p={2} sx={(t) => ({ background: t.palette.grey.A100 })}>
        {/* {breakdown[BreakdownKeys.BasePrice] != breakdown[BreakdownKeys.Subtotal] && (
          <BreakdownRow
            key={BreakdownKeys.BasePrice}
            value={breakdown[BreakdownKeys.BasePrice]}
            defaultValue="Base Price:"
            currencyCode={currencyCode}
            taxName={taxName}
          />
        )}

        <BreakdownRow
          key={BreakdownKeys.Protection}
          value={breakdown[BreakdownKeys.Protection]}
          defaultValue="Protection:"
          currencyCode={currencyCode}
          taxName={taxName}
        /> */}

        {/* We need to show these as they are not included in the quote price which gets displayed above. */}
        <BreakdownRow
          pad
          key={BreakdownKeys.IOSSAdminFee}
          value={breakdown[BreakdownKeys.IOSSAdminFee]}
          defaultValue="IOSS Admin Fee:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          pad
          key={BreakdownKeys.CustomsHandlingFee}
          value={breakdown[BreakdownKeys.CustomsHandlingFee]}
          defaultValue="Customs Handling Fee:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          pad
          key={BreakdownKeys.CustomsHandlingFeeVat}
          value={breakdown[BreakdownKeys.CustomsHandlingFeeVat]}
          defaultValue="Customs Handling Fee VAT:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        {/* We need to show this as its explicitly split out in the API. */}
        <BreakdownRow
          pad
          key={BreakdownKeys.SameDayCollection}
          value={breakdown[BreakdownKeys.SameDayCollection]}
          defaultValue="Same Day Collection:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        {/* Standard Summaries */}
        <BreakdownRow
          pad
          key={BreakdownKeys.Subtotal}
          value={breakdown[BreakdownKeys.Subtotal]}
          defaultValue="Sub Total:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          pad
          key={BreakdownKeys.Discount}
          value={breakdown[BreakdownKeys.Discount]}
          defaultValue="Discount:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          key={BreakdownKeys.Tax}
          value={breakdown[BreakdownKeys.Tax]}
          defaultValue="{{taxName}}:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          key={BreakdownKeys.DutiesAndTaxes}
          value={breakdown[BreakdownKeys.DutiesAndTaxes]}
          defaultValue="Duty and Local Taxes:"
          currencyCode={currencyCode}
          taxName={taxName}
        />

        <BreakdownRow
          key={BreakdownKeys.LocalTax}
          value={breakdown[BreakdownKeys.LocalTax]}
          defaultValue="Local Tax:"
          currencyCode={currencyCode}
          taxName={taxName}
        />
      </P2GBox>

      <P2GBox p={2} sx={(t) => ({ background: t.palette.grey.A200 })}>
        <FixedColumn
          left={
            <P2GText variant="body2">
              <strong>
                <Trans t={t} i18nKey="PROVIDER_GROUP_BREAKDOWN_TOTAL" defaults="Total:" />
              </strong>
            </P2GText>
          }
          right={
            <P2GBox textAlign="right">
              <P2GText variant="body2">{toDisplayCurrency(currencyCode, breakdown[BreakdownKeys.Total])}</P2GText>
            </P2GBox>
          }
          width={undefined}
        />
      </P2GBox>
    </P2GBox>
  );
};

export default ProviderGroupSummaryTotalVerified;
